import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { getEventOccurrences, type EventOccurrencesResponse } from 'api/events/endpoints/getEventOccurrences';
import {
  discardAllFileOccurrences,
  discardAllProcessOccurrences,
  discardFileOccurrence,
  discardProcessOccurrence,
} from 'api/events/endpoints/discardOccurrences';
import FormatDateTime from 'components/common/FormatDateTime';
import { confirmDialog } from 'dialogs';
import { toError } from 'helpers/toError';

interface SurplusModalProps {
  readonly eventId: number;
  readonly onHide: () => void;
  /** Called after anything is discarded, so the card behind can re-read itself. */
  readonly onDiscarded: () => void;
}

/**
 * The occurrences making up an event's surplus, and the only place in the Live
 * Events surface where process and file events differ.
 *
 * The asymmetry is the backend's, not a UI choice. A process occurrence is
 * interchangeable and the clear route always takes the OLDEST outstanding one
 * whatever id it is given, so the honest affordance is "Discard one", which
 * decrements the surplus. A file arrival is a distinct named thing and can be
 * discarded specifically, so those get a per-row button.
 */
export const SurplusModal = ({ eventId, onHide, onDiscarded }: SurplusModalProps) => {
  const [data, setData] = useState<EventOccurrencesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await getEventOccurrences(eventId));
    } catch (loadError) {
      setError(toError(loadError).message);
    }
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (discard: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await discard();
      toast.success(success);
      onDiscarded();
      await load();
    } catch (discardError) {
      toast.error(toError(discardError).message);
    } finally {
      setBusy(false);
    }
  };

  const isFile = data?.kind === 'file';
  const occurrences = data?.occurrences ?? [];

  const discardOne = async () => {
    const [oldest] = occurrences;
    if (oldest === undefined || data === null) {
      return;
    }
    const confirmed = await confirmDialog({
      title: 'Discard one occurrence',
      body: `Discard the oldest outstanding occurrence of "${data.event.name}"? Anything still waiting for it will keep waiting.`,
      confirmText: 'Discard one',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;
    // The oldest id, because that is the one the backend will actually clear —
    // so the row that disappears is the row the operator was looking at.
    await run(() => discardProcessOccurrence(oldest.occurrence_id), 'Discarded one occurrence.');
  };

  const discardFile = async (occurrenceId: number, filePath: string | null) => {
    if (data === null) return;
    const confirmed = await confirmDialog({
      title: 'Discard arrival',
      body: `Discard "${filePath ?? occurrenceId}"? Anything still waiting for it will keep waiting.`,
      confirmText: 'Discard',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;
    await run(() => discardFileOccurrence(occurrenceId), 'Discarded the arrival.');
  };

  const discardAll = async () => {
    if (data === null) return;
    // The count is the LIST's length, but the command clears everything
    // outstanding. Where the list is capped those differ, so a truncated list
    // says "all outstanding" rather than asserting a number known to be too
    // small. The warning above the table carries the same fact, but this
    // confirm is the last thing read before something irreversible.
    const scope = data.truncated ? 'all outstanding occurrences' : `all ${occurrences.length} outstanding occurrence${occurrences.length === 1 ? '' : 's'}`;
    const confirmed = await confirmDialog({
      title: 'Discard all',
      body: `Discard ${scope} of "${data.event.name}"? Anything still waiting for them will keep waiting. This cannot be undone.`,
      confirmText: 'Discard all',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;
    const { zone_name: zoneName, name } = data.event;
    await run(
      () => (isFile ? discardAllFileOccurrences(zoneName, name) : discardAllProcessOccurrences(zoneName, name)),
      'Discarded all outstanding occurrences.'
    );
  };

  return (
    <Modal show onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Outstanding occurrences{data === null ? '' : ` — ${data.event.name}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error !== null && <Alert variant="danger">{error}</Alert>}
        {error === null && data === null && <Spinner animation="border" size="sm" />}

        {data !== null && occurrences.length === 0 && <p className="text-muted mb-0">Nothing is outstanding.</p>}

        {data !== null && occurrences.length > 0 && (
          <>
            {data.truncated && <Alert variant="warning">Showing the first {data.limit}. There are more outstanding than this list can show.</Alert>}
            <Table size="sm" responsive className="mb-0">
              <thead>
                <tr>
                  <th>Raised</th>
                  <th>{isFile ? 'File' : 'Raised by'}</th>
                  {isFile && <th aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {occurrences.map((occurrence) => (
                  <tr key={occurrence.occurrence_id}>
                    <td>
                      <FormatDateTime value={occurrence.created_at} />
                    </td>
                    <td>{isFile ? occurrence.file_path : (occurrence.raiser_node_name ?? occurrence.raiser_comment ?? '—')}</td>
                    {isFile && (
                      <td className="text-end">
                        <Button variant="link" size="sm" disabled={busy} onClick={() => void discardFile(occurrence.occurrence_id, occurrence.file_path)}>
                          Discard
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!isFile && occurrences.length > 0 && (
          <Button variant="outline-danger" disabled={busy} onClick={() => void discardOne()}>
            Discard one
          </Button>
        )}
        {occurrences.length > 0 && (
          <Button variant="danger" disabled={busy} onClick={() => void discardAll()}>
            Discard all
          </Button>
        )}
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
